import { Button, Flex, Input, Selector, SelectionOption } from '@/components'
import { HorizontalSelectedList } from '@/components/core/HorizontalSelectedList/HorizontalSelectedList.component'
import { useSelectedList } from '@/components/core/HorizontalSelectedList/HorizontalSelectedList.hooks'
import { TagsList, TagsListItem } from '@/components/core/TagsList/TagsList.component'
import { BaseModal } from '@/components/shared/Modal/Modal.component'
import { useInput } from '@/hooks'
import { Alert, Api } from '@/modules'
import { Earth } from '@icon-park/react'
import { UserData, UserResponseRole, UserWithCompaniesResponse } from '@shared/models'
import { useEffect, useState } from 'react'
import { AdminAllCompaniesTag } from './Staff.styles'

const roleOptions: SelectionOption[] = [
  { value: UserResponseRole.Assessor, text: 'Assessor' },
  { value: UserResponseRole.Leadership, text: 'Chefia' },
  { value: UserResponseRole.Admin, text: 'Admin' },
  { value: UserResponseRole.AdminMaster, text: 'Admin Master' },
]

const regionOptions: SelectionOption[] = [
  { value: 'RJ', text: 'RJ' },
  { value: 'SP', text: 'SP' },
  { value: 'DF', text: 'DF' },
]

type EditStaffUserModalProps = {
  user: UserWithCompaniesResponse | null
  companyOptions: SelectionOption[]
  onClose: () => void
  onSaved: () => void
  onRemoveCompany: (userID: string, companyID: string) => void
  onAddCompany: (userID: string, companyID: string) => void
}

export function EditStaffUserModal({
  user,
  companyOptions,
  onClose,
  onSaved,
  onRemoveCompany,
  onAddCompany,
}: EditStaffUserModalProps) {
  const { sendAlert } = Alert.Hook()
  const { value: name, binder: nameBinder, reset: resetName } = useInput()
  const [role, setRole] = useState<SelectionOption>()
  const {
    binder: regionsBinder,
    selectedOptions: selectedRegions,
    setSelectedOptions: setSelectedRegions,
  } = useSelectedList(() => {})
  const [companySearchTerm, setCompanySearchTerm] = useState('')

  const { isRunning: isSaving, run: updateUser } = Api.Command<UserData>(Api.Methods.PUT, 'users/:id')

  useEffect(() => {
    if (!user) return
    resetName(user.userName)
    setRole(roleOptions.find((option) => option.value === user.role))
    setSelectedRegions(user.regions.map((region) => ({ value: region, text: region })))
  }, [user])

  const availableCompanyOptions =
    user ?
      companyOptions.filter(
        (company) =>
          !user.companies.some((linked) => linked.companyID === company.value) &&
          company.text.toLowerCase().includes(companySearchTerm.toLowerCase()),
      )
    : []

  function handleAddCompany(option?: SelectionOption): void {
    if (!user || !option) return
    onAddCompany(user.userID, String(option.value))
    setCompanySearchTerm('')
  }

  if (!user) return null

  function handleSave(): void {
    if (!user) return
    updateUser({
      binds: [['id', user.userID]],
      input: {
        name,
        email: user.userEmail,
        role: role?.value,
        regions: selectedRegions.map((region) => region.value),
      },
      onSuccess: () => {
        sendAlert({ type: 'success', title: 'Sucesso', message: 'Usuário atualizado com sucesso' })
        onSaved()
        onClose()
      },
    })
  }

  return (
    <BaseModal isOpen={!!user} onClose={onClose} title={`Editar ${user.userName}`} maxWidth="720px">
      <Flex $type="column" $align="stretch" $gap={24}>
        <Input label="Nome" placeholder="Nome do usuário" {...nameBinder} required />
        <Selector.Default
          label="Perfil"
          placeholder="Escolha o papel do usuário"
          options={roleOptions}
          selectedOption={role}
          onSelect={setRole}
          $fit
          required
        />
        <HorizontalSelectedList
          icon={Earth}
          label="Regiões"
          isLoading={false}
          placeholder="Selecione as regiões"
          options={regionOptions}
          preSelectedOptions={selectedRegions}
          {...regionsBinder}
        />
        {user.isAdmin ?
          <AdminAllCompaniesTag>Todas as empresas</AdminAllCompaniesTag>
        : <>
            <Selector.Searchable
              label="Adicionar empresa"
              placeholder="Buscar empresa para vincular"
              options={availableCompanyOptions}
              onSelect={handleAddCompany}
              onSearch={setCompanySearchTerm}
            />
            <Flex $type="column" $align="stretch" $gap={8}>
              <span>Empresas vinculadas</span>
              <TagsList
                emptyText="Nenhuma empresa vinculada"
                maxVisible={user.companies.length || 1}
                fullText
                tags={user.companies.map(
                  (company): TagsListItem => ({
                    id: company.companyID,
                    text: company.companyName,
                  }),
                )}
                onRemove={(tag) => onRemoveCompany(user.userID, tag.id)}
              />
            </Flex>
          </>
        }
        <Flex $type="row" $justify="flex-end" $align="center" $gap={16}>
          <Button.Root $variant="secondary" onClick={onClose} disabled={isSaving}>
            <Button.Text value="Cancelar" />
          </Button.Root>
          <Button.Root $variant="primary" onClick={handleSave} $loading={isSaving}>
            <Button.Text value="Salvar" />
          </Button.Root>
        </Flex>
      </Flex>
    </BaseModal>
  )
}
